import { saveAs } from 'file-saver';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ProposalColumnPatch,
  ProposalCompetitivePrice,
  ProposalItemChange,
  ProposalItemColumnType,
  ProposalItemLine,
  ProposalItemsQuery,
  ProposalItemsResponse,
  ProposalSpreadsheetErrorCode,
  ProposalSpreadsheetErrorResponse,
  ProposalSpreadsheetImportResponse,
  createProposalColumn,
  createProposalItemLine,
  deleteProposalItemLine,
  downloadProposalSpreadsheet,
  getProposalCompetitivePrice,
  getProposalItems,
  importProposalSpreadsheet,
  selectAllProposalItems,
  undoProposalSpreadsheet,
  updateProposalColumn,
  updateProposalItems,
} from 'services/actions/ai_management_proposal';

const SAVE_DEBOUNCE = 700;
const UNDO_TIMEOUT = 7000;

const keyOf = (lote: number | null, item: number | null) => `${lote ?? ''}#${item ?? ''}`;

export interface UndoToast {
  message: string;
  undo: () => void;
}

// data null = consulta terminou sem histórico; carregado distingue "nunca consultado" de
// "consultado e vazio".
export interface CompetitivePriceState {
  loading: boolean;
  data: ProposalCompetitivePrice | null;
  carregado: boolean;
}

interface Options {
  trackSave: <T>(request: Promise<T>) => Promise<T>;
  // Item que o "Corrigir" da checagem quer mostrar: a primeira carga abre na página dele.
  goTo?: { lote: number | null; item: number } | null;
}

export function useProposalItems(biddingId: number | null, { trackSave, goTo }: Options) {
  const [data, setData] = useState<ProposalItemsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState<ProposalItemsQuery>(() =>
    goTo ? { page: 1, ir_para_lote: goTo.lote ?? '', ir_para_item: goTo.item } : { page: 1 },
  );
  // Trocar o "ir para" pela página devolvida não precisa carregar de novo.
  const skipLoad = useRef(false);
  // Chave lote#item → mensagem do 422 do back.
  const [cellErrors, setCellErrors] = useState<Map<string, string>>(new Map());
  const [undoToast, setUndoToast] = useState<UndoToast | null>(null);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Chave lote#item; num Map para não consultar de novo enquanto já carrega.
  const [competitivePrice, setCompetitivePrice] = useState<Map<string, CompetitivePriceState>>(
    new Map(),
  );

  const [spreadsheetSummary, setSpreadsheetSummary] =
    useState<ProposalSpreadsheetImportResponse | null>(null);
  const [spreadsheetError, setSpreadsheetError] = useState<string | null>(null);
  const [spreadsheetBusy, setSpreadsheetBusy] = useState(false);

  const biddingRef = useRef(biddingId);
  biddingRef.current = biddingId;
  const pending = useRef<Map<string, ProposalItemChange>>(new Map());
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(
    (params: ProposalItemsQuery) => {
      if (!biddingId) return;
      const id = biddingId;
      setLoading(true);
      setError(false);
      getProposalItems(id, params)
        .then((res) => {
          if (biddingRef.current !== id) return;
          const status = (res as { status?: number })?.status ?? 200;
          const body = res?.data as ProposalItemsResponse & { error?: string };
          if (status >= 400 || body?.error || !Array.isArray(body?.itens)) {
            setError(true);
            return;
          }
          setData(body);
          if (params.ir_para_item !== undefined) {
            skipLoad.current = true;
            setQuery({ page: body.paginacao.page });
          }
        })
        .catch(() => {
          if (biddingRef.current === id) setError(true);
        })
        .finally(() => {
          if (biddingRef.current === id) setLoading(false);
        });
    },
    [biddingId],
  );

  useEffect(() => {
    if (skipLoad.current) {
      skipLoad.current = false;
      return;
    }
    load(query);
  }, [load, query]);

  const flush = useCallback(() => {
    if (!biddingId || pending.current.size === 0) return;
    const changes = Array.from(pending.current.values());
    const keys = changes.map((c) => keyOf(c.numero_lote, c.numero_item));
    pending.current.clear();
    trackSave(updateProposalItems(biddingId, changes))
      .then((res) => {
        if (biddingRef.current !== biddingId) return;
        // O interceptor global do Api resolve 4xx sem rejeitar. Erro de validação marca a célula
        // sem apagar a tabela.
        const status = (res as { status?: number })?.status ?? 200;
        const body = res?.data as ProposalItemsResponse & { error?: string };
        if (status >= 400 || body?.error) {
          const mensagem = body?.error || 'Não foi possível salvar. Tente de novo.';
          setCellErrors((prev) => {
            const next = new Map(prev);
            keys.forEach((k) => next.set(k, mensagem));
            return next;
          });
          return;
        }
        setData(body);
        setCellErrors((prev) => {
          const next = new Map(prev);
          keys.forEach((k) => next.delete(k));
          return next;
        });
      })
      .catch(() => {
        if (biddingRef.current !== biddingId) return;
        setCellErrors((prev) => {
          const next = new Map(prev);
          keys.forEach((k) => next.set(k, 'Não foi possível salvar. Tente de novo.'));
          return next;
        });
      });
  }, [biddingId, trackSave]);

  // Ao trocar de licitação ou sair da etapa, grava na hora o que estava esperando o debounce.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
        flush();
      }
    },
    [flush],
  );

  useEffect(
    () => () => {
      if (undoTimer.current) clearTimeout(undoTimer.current);
    },
    [],
  );

  const showUndo = useCallback((message: string, undo: () => void) => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndoToast({ message, undo });
    undoTimer.current = setTimeout(() => setUndoToast(null), UNDO_TIMEOUT);
  }, []);

  const dismissUndo = useCallback(() => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndoToast(null);
  }, []);

  const applyChange = useCallback(
    (change: ProposalItemChange) => {
      setData((prev) => {
        if (!prev) return prev;
        const itens = prev.itens.map((item) =>
          item.numero_lote === change.numero_lote && item.numero_item === change.numero_item
            ? { ...item, ...change }
            : item,
        );
        return { ...prev, itens };
      });

      const key = keyOf(change.numero_lote, change.numero_item);
      pending.current.set(key, { ...(pending.current.get(key) ?? change), ...change });
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, SAVE_DEBOUNCE);
    },
    [flush],
  );

  const openCompetitivePrice = useCallback(
    (item: { numero_lote: number | null; numero_item: number | null }) => {
      if (!biddingId) return;
      const id = biddingId;
      const key = keyOf(item.numero_lote, item.numero_item);
      const atual = competitivePrice.get(key);
      if (atual?.loading) return;

      setCompetitivePrice((prev) => {
        const next = new Map(prev);
        next.set(key, { loading: true, data: atual?.data ?? null, carregado: !!atual?.carregado });
        return next;
      });

      getProposalCompetitivePrice(id, item.numero_lote, item.numero_item)
        .then((res) => {
          if (biddingRef.current !== id) return;
          const status = (res as { status?: number })?.status ?? 200;
          const body = res?.data as (ProposalCompetitivePrice & { error?: string }) | undefined;
          const resultado = status >= 400 || body?.error ? null : body ?? null;
          setCompetitivePrice((prev) => {
            const next = new Map(prev);
            next.set(key, { loading: false, data: resultado, carregado: true });
            return next;
          });
        })
        .catch(() => {
          if (biddingRef.current !== id) return;
          setCompetitivePrice((prev) => {
            const next = new Map(prev);
            next.set(key, { loading: false, data: null, carregado: true });
            return next;
          });
        });
    },
    [biddingId, competitivePrice],
  );

  // Mesmo caminho do preço da tabela, reusando o "Desfazer"; casas decimais validadas pelo back.
  const aplicarPrecoCompetitivo = useCallback(
    (
      item: { numero_lote: number | null; numero_item: number | null },
      precoAnterior: number | null,
      novoPreco: number | null,
    ) => {
      applyChange({
        numero_lote: item.numero_lote,
        numero_item: item.numero_item,
        preco_unitario: novoPreco,
      });
      showUndo('Preço unitário atualizado.', () =>
        applyChange({
          numero_lote: item.numero_lote,
          numero_item: item.numero_item,
          preco_unitario: precoAnterior,
        }),
      );
    },
    [applyChange, showUndo],
  );

  const selectAll = useCallback(
    (selecionado: boolean, lote?: number | null) => {
      if (!biddingId) return;
      trackSave(selectAllProposalItems(biddingId, selecionado, lote ?? null))
        .then((res) => {
          if (biddingRef.current !== biddingId) return;
          const status = (res as { status?: number })?.status ?? 200;
          const body = res?.data as ProposalItemsResponse & { error?: string };
          // Erro (ex.: item isolado em julgamento por lote): ressincroniza sem apagar a tabela.
          if (status >= 400 || body?.error) {
            load(query);
            return;
          }
          setData(body);
        })
        .catch(() => {
          if (biddingRef.current === biddingId) load(query);
        });
    },
    [biddingId, trackSave, load, query],
  );

  const setFilters = useCallback((next: Partial<ProposalItemsQuery>) => {
    setQuery((prev) => ({ ...prev, ...next }));
  }, []);

  // Trata o 4xx resolvido pelo interceptor e devolve a mensagem de erro (ou null).
  const runWrite = useCallback(
    (request: Promise<{ data: unknown; status?: number }>): Promise<string | null> => {
      if (!biddingId) return Promise.resolve('Licitação não carregada.');
      return trackSave(request)
        .then((res) => {
          if (biddingRef.current !== biddingId) return null;
          const status = (res as { status?: number })?.status ?? 200;
          const body = res?.data as ProposalItemsResponse & { error?: string };
          if (status >= 400 || body?.error) return body?.error || 'Não foi possível salvar.';
          setData(body);
          return null;
        })
        .catch(() => 'Não foi possível salvar.');
    },
    [biddingId, trackSave],
  );

  const addColumn = useCallback(
    (nome: string, tipo: ProposalItemColumnType) =>
      biddingId ? runWrite(createProposalColumn(biddingId, nome, tipo)) : Promise.resolve('erro'),
    [biddingId, runWrite],
  );

  const updateColumn = useCallback(
    (id: string, patch: ProposalColumnPatch) =>
      biddingId ? runWrite(updateProposalColumn(biddingId, id, patch)) : Promise.resolve('erro'),
    [biddingId, runWrite],
  );

  // Exclui com excluida=true para o "Desfazer" restaurar: o back não apaga os valores dos itens.
  const removeColumn = useCallback(
    (id: string, nome: string) => {
      updateColumn(id, { excluida: true }).then((err) => {
        if (!err)
          showUndo(`Coluna "${nome}" excluída.`, () => updateColumn(id, { excluida: false }));
      });
    },
    [updateColumn, showUndo],
  );

  const addLine = useCallback(
    (linha: ProposalItemLine) =>
      biddingId ? runWrite(createProposalItemLine(biddingId, linha)) : Promise.resolve('erro'),
    [biddingId, runWrite],
  );

  const removeLine = useCallback(
    (linha: ProposalItemLine & { numero_item: number }) => {
      if (!biddingId) return;
      runWrite(deleteProposalItemLine(biddingId, linha.numero_item, linha.numero_lote)).then(
        (err) => {
          if (!err) {
            showUndo('Linha excluída.', () => {
              runWrite(createProposalItemLine(biddingId, linha));
            });
          }
        },
      );
    },
    [biddingId, runWrite, showUndo],
  );

  const deselectWithUndo = useCallback(
    (lote: number | null, item: number | null, porLote: boolean) => {
      if (porLote) {
        selectAll(false, lote);
        showUndo('Lote desmarcado.', () => selectAll(true, lote));
      } else {
        applyChange({ numero_lote: lote, numero_item: item, selecionado: false });
        showUndo('Item desmarcado.', () =>
          applyChange({ numero_lote: lote, numero_item: item, selecionado: true }),
        );
      }
    },
    [selectAll, applyChange, showUndo],
  );

  const filenameFromDisposition = (disposition: string | undefined, fallback: string) => {
    if (!disposition) return fallback;
    const match = /filename\*?=(?:UTF-8''|")?([^";]+)"?/i.exec(disposition);
    return match ? decodeURIComponent(match[1].trim()) : fallback;
  };

  const downloadSpreadsheet = useCallback(() => {
    if (!biddingId) return;
    setSpreadsheetBusy(true);
    downloadProposalSpreadsheet(biddingId)
      .then((res) => {
        if (biddingRef.current !== biddingId) return;
        const status = (res as { status?: number })?.status ?? 200;
        if (status >= 400 || !res?.data) {
          setSpreadsheetError('Não foi possível baixar a planilha. Tente de novo.');
          return;
        }
        const disposition = (res as { headers?: Record<string, string> })?.headers?.[
          'content-disposition'
        ];
        const filename = filenameFromDisposition(disposition, `proposta_itens_${biddingId}.xlsx`);
        saveAs(res.data, filename);
      })
      .catch(() => {
        if (biddingRef.current === biddingId)
          setSpreadsheetError('Não foi possível baixar a planilha. Tente de novo.');
      })
      .finally(() => {
        if (biddingRef.current === biddingId) setSpreadsheetBusy(false);
      });
  }, [biddingId]);

  const undoSpreadsheet = useCallback(() => {
    if (!biddingId) return;
    const id = biddingId;
    trackSave(undoProposalSpreadsheet(id))
      .then((res) => {
        if (biddingRef.current !== id) return;
        const status = (res as { status?: number })?.status ?? 200;
        const body = res?.data as ProposalItemsResponse & { error?: string };
        if (status >= 400 || body?.error) return;
        setSpreadsheetSummary(null);
        setData(body);
      })
      .catch(() => {});
  }, [biddingId, trackSave]);

  const importSpreadsheet = useCallback(
    (file: File) => {
      if (!biddingId) return;
      const id = biddingId;
      setSpreadsheetBusy(true);
      setSpreadsheetError(null);
      trackSave(importProposalSpreadsheet(id, file))
        .then((res) => {
          if (biddingRef.current !== id) return;
          const status = (res as { status?: number })?.status ?? 200;
          const body = res?.data as
            | ProposalSpreadsheetImportResponse
            | ProposalSpreadsheetErrorResponse
            | undefined;
          const erro = (body as ProposalSpreadsheetErrorResponse)?.erro as
            | ProposalSpreadsheetErrorCode
            | undefined;
          if (status >= 400 || erro) {
            setSpreadsheetError(erro || 'erro');
            return;
          }
          const importResult = body as ProposalSpreadsheetImportResponse;
          setSpreadsheetSummary(importResult);
          load(query);
          showUndo(
            `Planilha importada: ${importResult.aplicadas} itens aplicados · ` +
              `${importResult.ignoradas.length} linhas ignoradas.`,
            undoSpreadsheet,
          );
        })
        .catch(() => {
          if (biddingRef.current === id) setSpreadsheetError('erro');
        })
        .finally(() => {
          if (biddingRef.current === id) setSpreadsheetBusy(false);
        });
    },
    [biddingId, trackSave, load, query, showUndo, undoSpreadsheet],
  );

  const dismissSpreadsheetSummary = useCallback(() => setSpreadsheetSummary(null), []);
  const dismissSpreadsheetError = useCallback(() => setSpreadsheetError(null), []);

  return {
    data,
    loading,
    error,
    cellErrors,
    query,
    undoToast,
    spreadsheetSummary,
    spreadsheetError,
    spreadsheetBusy,
    competitivePrice,
    setFilters,
    applyChange,
    selectAll,
    addColumn,
    updateColumn,
    removeColumn,
    addLine,
    removeLine,
    deselectWithUndo,
    dismissUndo,
    downloadSpreadsheet,
    importSpreadsheet,
    undoSpreadsheet,
    dismissSpreadsheetSummary,
    dismissSpreadsheetError,
    openCompetitivePrice,
    aplicarPrecoCompetitivo,
  };
}
